import axios from 'axios';
import { useRef, useState } from 'react';

import Button from '~/components/common/Button';
import { TOURNAMENTS } from '~/constants';
import { useGameContext } from '~/contextProviders/GameContextProvider';
import { useGetResultsByWarrior } from '~/hooks/game/useGetResultsByWarrior';
import { useGetWarrior } from '~/hooks/game/useGetWarrior';
import { useGetAccount } from '~/hooks/web3/useGetAccount';

import Warrior from '../warrior/Warrior';
import BattleMatchup from './BattleMatchup';
import BattleResultStatus from './BattleResultStatus';

export default function BattleResult({
  battle,
  ownerWarriorId,
  opponentId,
  animate,
  revealed,
  onRevealResult,
}: {
  battle: number;
  ownerWarriorId: number;
  opponentId: number;
  animate?: boolean;
  revealed?: boolean;
  onRevealResult?: () => void;
}) {
  const [isActive, setIsActive] = useState(false);
  const [resultVisible, setResultVisible] = useState(!animate);
  const [revealerFinshed, setRevealerFinshed] = useState(!animate);

  const [shareClicked, setShareClicked] = useState(false);
  const [shareURL, setShareURL] = useState('');

  const revealerRef = useRef<HTMLVideoElement>(null);
  const { data: warrior } = useGetWarrior(ownerWarriorId);
  const { data: pair, isLoading } = useGetWarrior(opponentId);
  const { data: connectedAccount } = useGetAccount();
  const { contextId, areResultsReady, areWaitingForResults, tournamentParam } =
    useGameContext();

  const { data: warriorResult } = useGetResultsByWarrior({
    data: {
      contextId: contextId,
      battleId: battle,
      warriorId: warrior?.warrior_id || 0,
    },
    enabled: !!warrior?.warrior_id && areResultsReady,
  });

  // set warrior.owner address of current user to allow ens lookup at low level component
  if (warrior) {
    warrior.owner = connectedAccount?.address;
  }

  if (
    areWaitingForResults ||
    !warriorResult ||
    !warriorResult?.results.length
  ) {
    return (
      <BattleMatchup
        ownerWarriorId={ownerWarriorId}
        opponentWarriorId={opponentId}
      />
    );
  }

  const result = warriorResult.results[0];

  const handleShare = async () => {
    setShareClicked(true);
    const url = `/api/matchup/outcome?contextId=${contextId}&state=${battle}&warriorOneId=${warrior?.warrior_id}&warriorTwoId=${pair?.warrior_id}&type=winner&tournamentParam=${tournamentParam}`;
    const resp = await axios.get(url);
    const imageURL = await resp.data.url;
    setShareURL(imageURL);
    setShareClicked(false);
  };
  let isUserWinner = false;
  if (result?.victor) {
    isUserWinner = warrior?.warrior_id == result?.victor;
  }
  const hideShare = true;
  return (
    <>
      <div
        className={`battle-result ${
          resultVisible ?
            'battle-result--visible' :
            'battle-result--not-visible'
        } `}
        data-id={TOURNAMENTS[tournamentParam].id}
        onMouseEnter={() => setIsActive(true)}
        onMouseLeave={() => setIsActive(false)}
      >
        <div className="battle-result__body">
          <video
            muted={!animate}
            playsInline={true}
            className={`battle-result__revealer ${
              revealerFinshed ? 'battle-result__revealer--finished' : ''
            }`}
            onEnded={() => {
              setRevealerFinshed(true);
              setResultVisible(true);
            }}
            ref={revealerRef}
          >
            <source src="/reveal.mp4" type="video/mp4" />
          </video>
          {warrior && (
            <Warrior
              facing="east"
              warrior={warrior}
              active={isActive}
              victor={result?.victor}
            />
          )}
          <div className="battle-result__vs">
            <img src="/img/vs.icon.png" alt="VS" />
            {!revealed && (
              <Button
                className="warrior__attack__confirm__button warrior__attack__confirm__button--reveal primary"
                onClick={() => {
                  revealerRef.current?.play();
                  if (onRevealResult) onRevealResult();
                }}
                label="Reveal Result"
              />
            )}
          </div>
          {pair && (
            <Warrior
              facing="west"
              warrior={pair}
              active={isActive}
              victor={result?.victor}
            />
          )}
          {isLoading && (
            <div className="warrior warrior--west warrior--loading">
              Loading your Warriors...
            </div>
          )}
          {resultVisible && result && (
            <BattleResultStatus
              warrior={warrior}
              victor={result?.victor}
              announce={animate}
              combat={result?.entries}
            />
          )}
        </div>
      </div>
      {resultVisible && isUserWinner && !hideShare && (
        <div className="battle-result__share">
          {shareURL ? (
            <a
              className="warrior__attack__confirm__button primary"
              download
              href={shareURL}
              target="_blank"
              rel="noreferrer"
            >
              Download Image
            </a>
          ) : (
            <Button
              className="warrior__attack__confirm__button primary"
              label={
                shareClicked ? 'Generating image...' : 'Share your victory'
              }
              onClick={handleShare}
            />
          )}
        </div>
      )}
    </>
  );
}
